import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Button,
  Badge,
  toast,
  Select,
  TextInput,
} from '@etherisc/ui-kit';
import { Download, RefreshCw, Calendar } from 'lucide-react';
import { ledgerService } from '../../services/ledger/ledgerService';
import { useLedgerReports } from '../../hooks/ledger/useLedgerReports';
import { formatDate, formatCurrency } from '../../utils/formatters';

export function TrialBalanceReport() {
  const [selectedPeriod, setSelectedPeriod] = useState('2024-Q2');
  const [asOfDate, setAsOfDate] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const { periods, periodsLoading } = useLedgerReports();

  const {
    data: trialBalance,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['ledger', 'reports', 'trial-balance', selectedPeriod, asOfDate],
    queryFn: () => ledgerService.getTrialBalance({ 
      periodName: selectedPeriod,
      ...(asOfDate && { asOfDate }),
    }),
    enabled: !!selectedPeriod,
  });

  const handleExportCSV = useCallback(async () => {
    if (!trialBalance) return;

    setIsExporting(true);
    try {
      // Create CSV content
      const headers = ['Account Code', 'Account Name', 'Account Type', 'Debit Balance', 'Credit Balance'];
      const rows = trialBalance.accounts.map((account: unknown) => {
        const acc = account as { accountCode: string; accountName: string; accountType: string; debitBalance: string; creditBalance: string };
        return [
          acc.accountCode,
          acc.accountName,
          acc.accountType,
          acc.debitBalance,
          acc.creditBalance,
        ];
      });
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(',')),
        '', // Empty line
        `Total Debits,,,${trialBalance.totalDebits},`,
        `Total Credits,,,${trialBalance.totalCredits},`,
        `Balanced,,,${trialBalance.isBalanced ? 'Yes' : 'No'},`,
      ].join('\n');

      // Create and download file
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `trial-balance-${selectedPeriod}-${trialBalance.asOfDate}.csv`;
      link.click();
      
      toast.success('Trial balance exported successfully');
    } catch {
      toast.error('Failed to export trial balance');
    } finally {
      setIsExporting(false);
    }
  }, [trialBalance, selectedPeriod]);

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-red-600">
              <p>Error loading trial balance: {error.message}</p>
              <Button onClick={() => refetch()} className="mt-4">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Trial Balance Report</h1>
          <p className="text-muted-foreground">
            Summary of all account balances for verification
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => refetch()} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {trialBalance && (
            <Button onClick={handleExportCSV} disabled={isExporting}>
              <Download className="h-4 w-4 mr-2" />
              {isExporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Report Parameters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Select
                label="Accounting Period"
                value={selectedPeriod}
                onValueChange={(value: string) => setSelectedPeriod(value)}
                disabled={periodsLoading}
                options={periods?.map((period: unknown) => {
                  const p = period as { name: string; startDate: string; endDate: string };
                  return {
                    value: p.name,
                    label: `${p.name} (${formatDate(p.startDate)} - ${formatDate(p.endDate)})`
                  };
                }) || []}
              />
            </div>
            <div className="flex-1">
              <TextInput
                label="As Of Date (Optional)"
                type="date"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
              />
            </div>
            <Button onClick={() => refetch()}>
              <Calendar className="h-4 w-4 mr-2" />
              Generate Report
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Trial Balance Results */}
      {isLoading ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">Generating trial balance...</div>
          </CardContent>
        </Card>
      ) : trialBalance ? (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Trial Balance - {trialBalance.periodName}</CardTitle>
                <p className="text-sm text-muted-foreground">
                  As of {formatDate(trialBalance.asOfDate)}
                </p>
              </div>
              <Badge variant={trialBalance.isBalanced ? 'success' : 'destructive'}>
                {trialBalance.isBalanced ? 'Balanced' : 'Unbalanced'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3">Account Code</th>
                    <th className="text-left p-3">Account Name</th>
                    <th className="text-left p-3">Type</th>
                    <th className="text-right p-3">Debit Balance</th>
                    <th className="text-right p-3">Credit Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {trialBalance.accounts.map((account: unknown) => {
                    const acc = account as { accountCode: string; accountName: string; accountType: string; debitBalance: string; creditBalance: string };
                    return (
                    <tr key={acc.accountCode} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-mono">{acc.accountCode}</td>
                      <td className="p-3">{acc.accountName}</td>
                      <td className="p-3">
                        <Badge variant="outline">{acc.accountType}</Badge>
                      </td>
                      <td className="p-3 text-right font-mono">
                        {acc.debitBalance !== '0.00' ? formatCurrency(parseFloat(acc.debitBalance)) : '-'}
                      </td>
                      <td className="p-3 text-right font-mono">
                        {acc.creditBalance !== '0.00' ? formatCurrency(parseFloat(acc.creditBalance)) : '-'}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 font-semibold bg-gray-50">
                    <td colSpan={3} className="p-3">Total</td>
                    <td className="p-3 text-right font-mono">
                      {formatCurrency(parseFloat(trialBalance.totalDebits))}
                    </td>
                    <td className="p-3 text-right font-mono">
                      {formatCurrency(parseFloat(trialBalance.totalCredits))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {!trialBalance.isBalanced && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
                <p className="text-red-800 font-semibold">
                  ⚠️ Trial Balance is Unbalanced
                </p>
                <p className="text-red-700 text-sm mt-1">
                  Debits ({formatCurrency(parseFloat(trialBalance.totalDebits))}) do not equal 
                  Credits ({formatCurrency(parseFloat(trialBalance.totalCredits))}).
                  Please review journal entries for errors.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
